import { TILE } from '../config/tuning'
import type { World } from '../ecs/world'
import type { LevelData } from '../world/level'
import { clamp, lerp } from '../core/math'
import { ASSETS } from './assets'
import { PALETTE } from './palette'
import { Tile, isTeleporter } from '../world/tiles'

/** deterministic per-tile hash for subtle floor variation */
function tileHash(x: number, y: number): number {
  let h = (x * 374761393 + y * 668265263) | 0
  h = (h ^ (h >> 13)) * 1274126177
  return ((h ^ (h >> 16)) >>> 0) / 4294967296
}

/**
 * Canvas renderer behind a thin interface: reads World, resolves renderIds
 * through the asset manifest, camera-follows players()[0] (v1). A future
 * PixiJS/sprite swap replaces this file + assets.ts only.
 */
export class Renderer {
  private ctx: CanvasRenderingContext2D
  camX = 0
  camY = 0
  private scale = 2
  private inited = false
  time = 0
  // exit-tile lookup cached per level (see drawObjectiveChevron)
  private exitLevel: LevelData | null = null
  private exitTile: { x: number; y: number } | null = null

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas 2D unavailable')
    this.ctx = ctx
    this.resize()
    window.addEventListener('resize', () => this.resize())
  }

  resize(): void {
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    this.canvas.width = Math.floor(window.innerWidth * dpr)
    this.canvas.height = Math.floor(window.innerHeight * dpr)
    this.canvas.style.width = `${window.innerWidth}px`
    this.canvas.style.height = `${window.innerHeight}px`
    // ~15 tiles visible vertically on desktop; fewer on short (phone) screens
    // so actors stay finger-sized rather than shrinking to the viewport
    const rows = window.innerHeight < 560 ? 11 : 15
    this.scale = Math.max(1.2, this.canvas.height / (rows * TILE))
  }

  screenToWorld(sx: number, sy: number): { x: number; y: number } {
    const dpr = this.canvas.width / window.innerWidth
    return {
      x: this.camX + (sx * dpr - this.canvas.width / 2) / this.scale,
      y: this.camY + (sy * dpr - this.canvas.height / 2) / this.scale
    }
  }

  render(world: World, alpha: number, dtRender: number): void {
    this.time += dtRender
    const ctx = this.ctx
    const cw = this.canvas.width
    const ch = this.canvas.height

    // camera follows first player, clamped to level bounds
    const p = world.players()[0] ?? world.entities.find((e) => e.kind === 'player')
    if (p) {
      const tx = lerp(p.px, p.x, alpha)
      const ty = lerp(p.py, p.y, alpha)
      if (!this.inited) {
        this.camX = tx
        this.camY = ty
        this.inited = true
      } else {
        this.camX = lerp(this.camX, tx, 0.12)
        this.camY = lerp(this.camY, ty, 0.12)
      }
    }
    const viewW = cw / this.scale
    const viewH = ch / this.scale
    const mapW = world.map.w * TILE
    const mapH = world.map.h * TILE
    this.camX = mapW <= viewW ? mapW / 2 : clamp(this.camX, viewW / 2, mapW - viewW / 2)
    this.camY = mapH <= viewH ? mapH / 2 : clamp(this.camY, viewH / 2, mapH - viewH / 2)

    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.fillStyle = PALETTE.void
    ctx.fillRect(0, 0, cw, ch)

    // shake
    let shx = 0
    let shy = 0
    if (world.shake > 0.2) {
      shx = (tileHash(Math.floor(this.time * 60), 1) - 0.5) * world.shake * 2
      shy = (tileHash(2, Math.floor(this.time * 60)) - 0.5) * world.shake * 2
    }
    ctx.setTransform(this.scale, 0, 0, this.scale, cw / 2 - this.camX * this.scale + shx, ch / 2 - this.camY * this.scale + shy)

    this.drawTiles(world)
    // LINGERING SANCTION fields: translucent sanction ring under the entities
    for (const f of world.fields) {
      const g2 = ctx.createRadialGradient(f.x, f.y, 4, f.x, f.y, f.r)
      g2.addColorStop(0, 'rgba(65, 224, 255, 0.16)')
      g2.addColorStop(1, 'rgba(65, 224, 255, 0)')
      ctx.fillStyle = g2
      ctx.beginPath()
      ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2)
      ctx.fill()
      ctx.strokeStyle = 'rgba(65, 224, 255, 0.35)'
      ctx.lineWidth = 1.5
      ctx.stroke()
    }
    this.drawTelegraphs(world)
    this.drawEntities(world, alpha)
    this.drawParticles(world)

    // BLACKOUT directive: a dark radial shroud closes in around the player —
    // the cameras can see in the dark; participants cannot
    if (world.floorMods.blackoutRadius > 0 && p) {
      const bx = lerp(p.px, p.x, alpha)
      const by = lerp(p.py, p.y, alpha)
      const br = world.floorMods.blackoutRadius
      const dark = world.tuning.endless.directives.blackout.maxDarkness
      const g = ctx.createRadialGradient(bx, by, br * 0.45, bx, by, br)
      g.addColorStop(0, 'rgba(4,6,10,0)')
      g.addColorStop(1, `rgba(4,6,10,${dark})`)
      ctx.fillStyle = g
      ctx.fillRect(this.camX - viewW / 2 - TILE, this.camY - viewH / 2 - TILE, viewW + TILE * 2, viewH + TILE * 2)
    }

    ctx.setTransform(1, 0, 0, 1, 0, 0)

    // off-screen objective chevron — screen space, drawn last so it sits
    // above the BLACKOUT shroud and the objective stays findable in the dark
    this.drawObjectiveChevron(world)
  }

  /**
   * Edge chevron toward the current objective when it is off-screen: the exit
   * once it is open, else (on production-gated floors) the nearest living
   * assembler, in hazard amber. One chevron at a time; knobs in tuning.feel.
   */
  private drawObjectiveChevron(world: World): void {
    const f = world.tuning.feel
    let wx: number
    let wy: number
    let color: string
    if (world.exitOpen) {
      if (this.exitLevel !== world.level) {
        this.exitLevel = world.level
        this.exitTile = null
        for (const t of world.map.tilesOfType(Tile.Exit)) {
          this.exitTile = t
          break
        }
      }
      if (!this.exitTile) return
      wx = this.exitTile.x * TILE + TILE / 2
      wy = this.exitTile.y * TILE + TILE / 2
      color = PALETTE.exit
    } else if (world.level.exit.type === 'generators') {
      const p = world.players()[0]
      const fx = p ? p.x : this.camX
      const fy = p ? p.y : this.camY
      let bx = 0
      let by = 0
      let bd = Infinity
      for (const g of world.generators()) {
        const d = (g.x - fx) ** 2 + (g.y - fy) ** 2
        if (d < bd) {
          bd = d
          bx = g.x
          by = g.y
        }
      }
      if (bd === Infinity) return
      wx = bx
      wy = by
      color = PALETTE.alertAmber
    } else {
      return
    }

    const cw = this.canvas.width
    const ch = this.canvas.height
    const sx = cw / 2 + (wx - this.camX) * this.scale
    const sy = ch / 2 + (wy - this.camY) * this.scale
    if (sx >= 0 && sx <= cw && sy >= 0 && sy <= ch) return // on-screen — the world drawing carries it

    const dpr = cw / window.innerWidth
    const m = f.chevronMargin * dpr
    const cx = clamp(sx, m, cw - m)
    const cy = clamp(sy, m, ch - m)
    const ang = Math.atan2(sy - cy, sx - cx)
    const s = f.chevronSize * dpr
    // gentle alpha pulse; static under reduced motion
    const alpha = world.reducedMotion
      ? f.chevronAlpha
      : f.chevronMinAlpha + (f.chevronAlpha - f.chevronMinAlpha) * (0.5 + 0.5 * Math.sin(this.time * Math.PI * 2 * f.chevronPulseHz))
    const ctx = this.ctx
    ctx.save()
    ctx.translate(cx, cy)
    ctx.rotate(ang)
    ctx.globalAlpha = alpha
    ctx.strokeStyle = color
    ctx.lineWidth = Math.max(2, 2.5 * dpr)
    ctx.lineJoin = 'round'
    ctx.lineCap = 'round'
    // double chevron pointing along +x, matching the exit tile's descend marks
    ctx.beginPath()
    ctx.moveTo(-s * 0.2, -s)
    ctx.lineTo(s * 0.8, 0)
    ctx.lineTo(-s * 0.2, s)
    ctx.moveTo(-s, -s)
    ctx.lineTo(0, 0)
    ctx.lineTo(-s, s)
    ctx.stroke()
    ctx.restore()
  }

  private drawTiles(world: World): void {
    const ctx = this.ctx
    const cw = this.canvas.width
    const ch = this.canvas.height
    const x0 = Math.max(0, Math.floor((this.camX - cw / 2 / this.scale) / TILE) - 1)
    const x1 = Math.min(world.map.w - 1, Math.ceil((this.camX + cw / 2 / this.scale) / TILE) + 1)
    const y0 = Math.max(0, Math.floor((this.camY - ch / 2 / this.scale) / TILE) - 1)
    const y1 = Math.min(world.map.h - 1, Math.ceil((this.camY + ch / 2 / this.scale) / TILE) + 1)

    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        const t = world.map.at(tx, ty)
        const px = tx * TILE
        const py = ty * TILE
        switch (t) {
          case Tile.Void:
            break
          case Tile.Wall:
          case Tile.Destructible: {
            // neighbor-aware faces so wall runs read as continuous mass
            const solidUp = world.map.solidAt(tx, ty - 1)
            const solidDown = world.map.solidAt(tx, ty + 1)
            const solidLeft = world.map.solidAt(tx - 1, ty)
            const solidRight = world.map.solidAt(tx + 1, ty)
            ctx.fillStyle = t === Tile.Destructible ? '#333d2e' : PALETTE.wall
            ctx.fillRect(px, py, TILE, TILE)
            ctx.fillStyle = t === Tile.Destructible ? '#414d38' : PALETTE.wallTop
            if (!solidUp) ctx.fillRect(px, py, TILE, TILE * 0.4)
            ctx.fillStyle = PALETTE.wallEdge
            if (!solidDown) ctx.fillRect(px, py + TILE - 3, TILE, 3)
            if (!solidLeft) ctx.fillRect(px, py, 2, TILE)
            if (!solidRight) ctx.fillRect(px + TILE - 2, py, 2, TILE)
            if (t === Tile.Destructible) {
              ctx.strokeStyle = 'rgba(10,14,10,0.8)'
              ctx.lineWidth = 1.5
              ctx.beginPath()
              ctx.moveTo(px + 6, py + 8)
              ctx.lineTo(px + 14, py + 18)
              ctx.lineTo(px + 10, py + 26)
              ctx.moveTo(px + 22, py + 6)
              ctx.lineTo(px + 19, py + 16)
              ctx.lineTo(px + 26, py + 24)
              ctx.stroke()
            }
            break
          }
          case Tile.Door:
          case Tile.LockedDoor: {
            this.floorTile(tx, ty)
            const locked = t === Tile.LockedDoor
            ctx.fillStyle = '#0d0f13'
            ctx.fillRect(px, py, TILE, TILE)
            ctx.fillStyle = locked ? PALETTE.doorLocked : PALETTE.door
            ctx.fillRect(px + 3, py + 3, TILE - 6, TILE - 6)
            ctx.strokeStyle = '#0d0f13'
            ctx.lineWidth = 2
            ctx.strokeRect(px + 3, py + 3, TILE - 6, TILE - 6)
            ctx.beginPath()
            ctx.moveTo(px + TILE / 2, py + 4)
            ctx.lineTo(px + TILE / 2, py + TILE - 4)
            ctx.stroke()
            if (locked) {
              ctx.fillStyle = '#3a2a06'
              ctx.beginPath()
              ctx.arc(px + TILE / 2, py + TILE / 2, 4, 0, Math.PI * 2)
              ctx.fill()
            }
            break
          }
          case Tile.Exit: {
            this.floorTile(tx, ty)
            const open = world.exitOpen
            const pulse = open ? 0.55 + Math.sin(this.time * 4) * 0.35 : 0.18
            const g = ctx.createRadialGradient(px + TILE / 2, py + TILE / 2, 2, px + TILE / 2, py + TILE / 2, TILE * 0.7)
            g.addColorStop(0, open ? PALETTE.exit : '#26483a')
            g.addColorStop(1, 'rgba(87,230,168,0)')
            ctx.globalAlpha = pulse
            ctx.fillStyle = g
            ctx.fillRect(px - 4, py - 4, TILE + 8, TILE + 8)
            ctx.globalAlpha = 1
            ctx.strokeStyle = open ? PALETTE.exit : '#26483a'
            ctx.lineWidth = 2
            ctx.strokeRect(px + 5, py + 5, TILE - 10, TILE - 10)
            // descend chevrons
            ctx.beginPath()
            ctx.moveTo(px + 10, py + 12)
            ctx.lineTo(px + TILE / 2, py + 18)
            ctx.lineTo(px + TILE - 10, py + 12)
            ctx.moveTo(px + 10, py + 18)
            ctx.lineTo(px + TILE / 2, py + 24)
            ctx.lineTo(px + TILE - 10, py + 18)
            ctx.stroke()
            break
          }
          case Tile.Trap: {
            this.floorTile(tx, ty)
            const armed = 0.5 + Math.sin(this.time * 5 + tx * 2 + ty) * 0.3
            ctx.fillStyle = `rgba(138,48,56,${0.25 + armed * 0.2})`
            ctx.fillRect(px + 2, py + 2, TILE - 4, TILE - 4)
            ctx.fillStyle = PALETTE.trap
            for (let i = 0; i < 3; i++) {
              for (let j = 0; j < 3; j++) {
                ctx.beginPath()
                const sx = px + 8 + i * 8
                const sy = py + 8 + j * 8
                ctx.moveTo(sx - 2.5, sy + 2)
                ctx.lineTo(sx, sy - 3 * armed - 1)
                ctx.lineTo(sx + 2.5, sy + 2)
                ctx.closePath()
                ctx.fill()
              }
            }
            break
          }
          case Tile.Rubble: {
            this.floorTile(tx, ty)
            ctx.fillStyle = PALETTE.rubble
            for (let i = 0; i < 5; i++) {
              const h = tileHash(tx * 7 + i, ty * 13 + i)
              ctx.beginPath()
              ctx.arc(px + 6 + h * 20, py + 6 + tileHash(ty + i, tx) * 20, 2 + h * 3, 0, Math.PI * 2)
              ctx.fill()
            }
            break
          }
          case Tile.OpenDoor: {
            this.floorTile(tx, ty)
            ctx.fillStyle = '#0d0f13'
            ctx.fillRect(px, py, 3, TILE)
            ctx.fillRect(px + TILE - 3, py, 3, TILE)
            break
          }
          case Tile.Relay:
          case Tile.RelayOn: {
            this.floorTile(tx, ty)
            const on = t === Tile.RelayOn
            const ph = on ? 1 : 0.4 + Math.sin(this.time * 2.5 + tx + ty) * 0.25
            ctx.fillStyle = '#0d0f13'
            ctx.fillRect(px + 6, py + 6, TILE - 12, TILE - 12)
            ctx.strokeStyle = PALETTE.relay
            ctx.globalAlpha = on ? 1 : 0.35 + ph * 0.4
            ctx.lineWidth = 2
            ctx.strokeRect(px + 6, py + 6, TILE - 12, TILE - 12)
            ctx.fillStyle = PALETTE.relay
            ctx.beginPath()
            ctx.arc(px + TILE / 2, py + TILE / 2, on ? 5 : 3 + ph * 2, 0, Math.PI * 2)
            ctx.fill()
            ctx.globalAlpha = 1
            break
          }
          case Tile.Shutter: {
            // sealed containment shutter: wall-mass frame with lit slats
            ctx.fillStyle = '#141a22'
            ctx.fillRect(px, py, TILE, TILE)
            ctx.fillStyle = PALETTE.wallEdge
            ctx.fillRect(px, py, TILE, 3)
            ctx.fillRect(px, py + TILE - 3, TILE, 3)
            ctx.strokeStyle = PALETTE.relay
            ctx.globalAlpha = 0.4 + Math.sin(this.time * 2 + tx * 1.7) * 0.15
            ctx.lineWidth = 2
            ctx.beginPath()
            for (let i = 1; i <= 3; i++) {
              ctx.moveTo(px + 4, py + i * (TILE / 4))
              ctx.lineTo(px + TILE - 4, py + i * (TILE / 4))
            }
            ctx.stroke()
            ctx.globalAlpha = 1
            break
          }
          case Tile.ShutterOpen: {
            this.floorTile(tx, ty)
            // retracted: only the frame remains, relay-lit
            ctx.fillStyle = '#0d0f13'
            ctx.fillRect(px, py, 3, TILE)
            ctx.fillRect(px + TILE - 3, py, 3, TILE)
            ctx.fillStyle = PALETTE.relay
            ctx.globalAlpha = 0.5
            ctx.fillRect(px, py, 3, 4)
            ctx.fillRect(px + TILE - 3, py, 3, 4)
            ctx.fillRect(px, py + TILE - 4, 3, 4)
            ctx.fillRect(px + TILE - 3, py + TILE - 4, 3, 4)
            ctx.globalAlpha = 1
            break
          }
          default: {
            if (isTeleporter(t)) {
              this.floorTile(tx, ty)
              const ph = 0.5 + Math.sin(this.time * 3 + t) * 0.3
              ctx.strokeStyle = PALETTE.teleporter
              ctx.globalAlpha = 0.35 + ph * 0.5
              ctx.lineWidth = 2
              ctx.beginPath()
              ctx.arc(px + TILE / 2, py + TILE / 2, 9 + ph * 3, 0, Math.PI * 2)
              ctx.stroke()
              ctx.beginPath()
              ctx.arc(px + TILE / 2, py + TILE / 2, 4, 0, Math.PI * 2)
              ctx.stroke()
              ctx.globalAlpha = 1
            } else {
              this.floorTile(tx, ty)
            }
          }
        }
      }
    }
  }

  private floorTile(tx: number, ty: number): void {
    const ctx = this.ctx
    const px = tx * TILE
    const py = ty * TILE
    const h = tileHash(tx, ty)
    ctx.fillStyle = PALETTE.floor
    ctx.fillRect(px, py, TILE, TILE)
    // subtle deterministic variation + seam lines
    ctx.fillStyle = `rgba(255,255,255,${h * 0.022})`
    ctx.fillRect(px, py, TILE, TILE)
    ctx.strokeStyle = PALETTE.floorLine
    ctx.lineWidth = 1
    ctx.strokeRect(px + 0.5, py + 0.5, TILE, TILE)
  }

  private drawTelegraphs(world: World): void {
    const ctx = this.ctx
    for (const e of world.entities) {
      if (e.dead || !e.boss || e.boss.stage !== 'telegraph') continue
      const b = e.boss
      if (b.currentAttack === 'charge') {
        // beam along charge direction up to the first wall
        let len = 0
        const maxLen = 22 * TILE
        while (len < maxLen) {
          const x = e.x + b.ax * len
          const y = e.y + b.ay * len
          if (world.map.solidAt(Math.floor(x / TILE), Math.floor(y / TILE))) break
          len += TILE / 2
        }
        const a = 0.16 + Math.sin(this.time * 16) * 0.07
        ctx.save()
        ctx.translate(e.x, e.y)
        ctx.rotate(Math.atan2(b.ay, b.ax))
        const g = ctx.createLinearGradient(0, 0, len, 0)
        g.addColorStop(0, `rgba(255,79,94,${a * 2})`)
        g.addColorStop(1, `rgba(255,79,94,${a * 0.4})`)
        ctx.fillStyle = g
        const w = e.radius * 1.7
        ctx.fillRect(0, -w / 2, len, w)
        ctx.strokeStyle = `rgba(255,79,94,${a * 3})`
        ctx.setLineDash([8, 6])
        ctx.lineWidth = 2
        ctx.strokeRect(0, -w / 2, len, w)
        ctx.restore()
      } else if (b.currentAttack === 'slam') {
        const a = 0.14 + Math.sin(this.time * 14) * 0.06
        ctx.save()
        ctx.fillStyle = `rgba(255,79,94,${a})`
        ctx.strokeStyle = `rgba(255,79,94,${a * 3})`
        ctx.setLineDash([10, 8])
        ctx.lineWidth = 2
        const rr = (b.def.params.slamRadius ?? 120) * (b.def.params.scale ?? 1)
        ctx.beginPath()
        ctx.arc(e.x, e.y, rr, 0, Math.PI * 2)
        ctx.fill()
        ctx.stroke()
        ctx.restore()
      }
    }
  }

  private drawEntities(world: World, alpha: number): void {
    const ctx = this.ctx
    const sorted = world.entities
      .filter((e) => !e.dead && e.kind !== 'fx')
      .sort((a, b) => {
        const za = a.kind === 'corpse' ? -1000 : a.kind === 'pickup' ? -500 : a.y
        const zb = b.kind === 'corpse' ? -1000 : b.kind === 'pickup' ? -500 : b.y
        return za - zb
      })
    for (const e of sorted) {
      const x = lerp(e.px, e.x, alpha)
      const y = lerp(e.py, e.y, alpha)
      const asset = ASSETS[e.renderId]
      if (!asset) continue
      // spawn-in scale pop
      if (e.spawnT > 0 && e.kind === 'enemy') {
        ctx.save()
        ctx.translate(x, y)
        const s = Math.max(0.2, 1 - e.spawnT * 1.8)
        ctx.scale(s, s)
        ctx.translate(-x, -y)
        asset.draw(ctx, e, x, y, this.time)
        ctx.restore()
      } else {
        asset.draw(ctx, e, x, y, this.time)
      }
    }
  }

  private drawParticles(world: World): void {
    const ctx = this.ctx
    for (const p of world.particles) {
      ctx.globalAlpha = Math.max(0, p.life / p.maxLife)
      ctx.fillStyle = p.color
      ctx.beginPath()
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.globalAlpha = 1
  }
}
